import {forwardRef, useEffect, useId, useLayoutEffect, useRef} from 'react'
import PropTypes from 'prop-types'

import {SparkleIcon} from './icons'

export const ANSWER_PLACEHOLDER = 'Type your answer. A few words is fine.'
// No "I read your story": it is true, but it reads as if someone else had (privacy).
export const FIRST_LEAD = 'A few quick questions your family would love answered.'
// A rich story can earn a single question.
export const ONLY_LEAD = 'One quick question your family would love answered.'
export const LATER_LEAD = 'Your family might want to know'

const PrevIcon = () => (
  <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.3' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
    <path d='m15 18-6-6 6-6' />
  </svg>
)

const NextIcon = () => (
  <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.3' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
    <path d='m9 18 6-6-6-6' />
  </svg>
)

/** Grows the answer box with its text, like the prototype's `fit`. */
const fit = textarea => {
  if (!textarea) return

  textarea.style.height = 'auto'
  textarea.style.height = `${textarea.scrollHeight}px`
}

/**
 * One interview question (prototype `famCardHtml9`): the lead line and
 * Previous / `k of n` / Next, "You said …" when the question builds on the
 * writer's words, the question, the answer box, example chips, and the one
 * button that moves on (Next / Skip / Write my story / Done).
 *
 * Enter in the box is that button; Shift+Enter adds a line. The box takes
 * focus whenever a different question opens. The ref is the card element,
 * for the view that positions it under the highlighted words.
 */
const EnrichQuestionCard = forwardRef(({
  item, index, count, goLabel, goPrimary, goDisabled = false, free, style, onAnswer, onExample, onGo, onJump
}, ref) => {
  const id = useId()
  const leadId = `${id}-lead`
  const questionId = `${id}-question`
  const answerId = `${id}-answer`
  const textareaRef = useRef(null)

  useEffect(() => {
    if (textareaRef.current) textareaRef.current.focus()
  }, [index])

  useLayoutEffect(() => fit(textareaRef.current), [item.answer, index])

  const handleKeyDown = event => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault()
      if (!goDisabled) onGo()
    }
  }

  // A chip fills the box and hands it focus with the caret at the end, so
  // the writer can keep going in their own words.
  const handleExample = example => {
    onExample(example)
    requestAnimationFrame(() => {
      const textarea = textareaRef.current

      if (!textarea) return

      textarea.focus()
      textarea.setSelectionRange(textarea.value.length, textarea.value.length)
    })
  }

  return (
    <div
      aria-labelledby={`${leadId} ${questionId}`}
      className={`ls-fam-card${free ? ' free' : ''}`}
      ref={ref}
      role='dialog'
      style={style}>
      <div className='ls-fam-who'>
        <span aria-hidden='true' className='ls-fam-av'><SparkleIcon size={16} /></span>
        <span id={leadId}>{index > 0 ? LATER_LEAD : count === 1 ? ONLY_LEAD : FIRST_LEAD}</span>
        <span className='ls-fam-nav'>
          <button aria-label='Previous question' className='ls-icon-btn ls-mini-btn ls-fam-arrow' disabled={index === 0} type='button' onClick={() => onJump(-1)}>
            <PrevIcon />
          </button>
          <span className='ls-fam-count'>{index + 1} of {count}</span>
          <button aria-label='Next question' className='ls-icon-btn ls-mini-btn ls-fam-arrow' disabled={index === count - 1} type='button' onClick={() => onJump(1)}>
            <NextIcon />
          </button>
        </span>
      </div>
      {item.quote && <p className='ls-fam-said'><span>You said</span>&ldquo;{item.quote}&rdquo;</p>}
      <p className='ls-fam-q' id={questionId}>{item.text}</p>
      <div className='ls-fam-field'>
        <label className='sr-only' htmlFor={answerId}>Your answer</label>
        <textarea
          id={answerId}
          placeholder={ANSWER_PLACEHOLDER}
          ref={textareaRef}
          rows={2}
          value={item.answer}
          onChange={event => onAnswer(event.target.value)}
          onKeyDown={handleKeyDown}
        />
      </div>
      {
        item.examples.length > 0 &&
          <div aria-label='Tap an answer to start with it' className='ls-f-chips ls-fam-chips' role='group'>
            {
              item.examples.map(example => (
                <button className='ls-f-chip' key={example} type='button' onClick={() => handleExample(example)}>{example}</button>
              ))
            }
          </div>
      }
      <div className='ls-fam-acts'>
        <button className={`ls-f-go small${goPrimary ? '' : ' ghost'}`} disabled={goDisabled} type='button' onClick={onGo}>{goLabel}</button>
      </div>
    </div>
  )
})

EnrichQuestionCard.displayName = 'EnrichQuestionCard'

EnrichQuestionCard.propTypes = {
  count: PropTypes.number.isRequired,
  free: PropTypes.bool,
  goDisabled: PropTypes.bool,
  goLabel: PropTypes.string.isRequired,
  goPrimary: PropTypes.bool,
  index: PropTypes.number.isRequired,
  item: PropTypes.shape({
    answer: PropTypes.string.isRequired,
    examples: PropTypes.arrayOf(PropTypes.string).isRequired,
    quote: PropTypes.string,
    text: PropTypes.string.isRequired
  }).isRequired,
  onAnswer: PropTypes.func.isRequired,
  onExample: PropTypes.func.isRequired,
  onGo: PropTypes.func.isRequired,
  onJump: PropTypes.func.isRequired,
  style: PropTypes.object
}

export default EnrichQuestionCard
